import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const CATEGORIES = ['All', 'Healthcare & Vision', 'AI Platforms', 'Automation & Agents', 'Research'];

const projectsData = [
  // ── Healthcare & Vision ──────────────────────────────────────────────────────
  {
    id: 'mbot',
    title: 'M-BOT',
    subtitle: 'Medical AI · AWS',
    category: 'Healthcare & Vision',
    description:
      'End-to-end AI malaria-detection platform built for Health Services Academy, Islamabad. Processes up to 30,000 patients/day at under $0.08 per patient. Includes a custom auto-focus module for stained blood-smear imaging.',
    impact: ['30k patients/day', '<$0.08 per patient', 'AWS production'],
    stack: ['PyTorch', 'AWS', 'Computer Vision', 'OpenCV'],
    links: { github: 'https://github.com/haiderrmasood99' },
    featured: true,
  },
  {
    id: 'osteodoc',
    title: 'OsteoDoc',
    subtitle: 'Research · IEEE Xplore 2024',
    category: 'Research',
    description:
      'Peer-reviewed knee-osteoarthritis grading pipeline. Published IEEE Xplore 2024 — 0.91 AUC, +12% over radiologist baseline. Grad-CAM explainability built in. Best Paper award at ICODT2; top honours at FICS and COMPPEC.',
    impact: ['0.91 AUC', '+12% vs radiologist', 'Best Paper — ICODT2'],
    stack: ['PyTorch', 'Grad-CAM', 'Medical Imaging', 'Python'],
    links: { github: 'https://github.com/haiderrmasood99', paper: 'https://ieeexplore.ieee.org' },
    featured: true,
  },
  {
    id: 'content-mod',
    title: 'Content Moderation',
    subtitle: 'Trust & Safety · AWS',
    category: 'Healthcare & Vision',
    description:
      'Video safety pipeline combining AWS Rekognition (timestamped visual labels) and Transcribe (speech-to-text). Lets reviewers jump to flagged moments rather than watching footage linearly — 40–70% review time saved.',
    impact: ['40–70% review time saved', 'Visual + audio signals', 'Timestamp-linked review'],
    stack: ['AWS Rekognition', 'AWS Transcribe', 'Python', 'Boto3', 'SageMaker'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  // ── AI Platforms ─────────────────────────────────────────────────────────────
  {
    id: 'campfire',
    title: 'Campfire Backend',
    subtitle: 'EdTech · AI Platform',
    category: 'AI Platforms',
    description:
      'Backend for an AI-powered collaborative study platform. Handles AI flashcard generation from uploaded files, quiz sessions, WebSocket real-time note editing, achievements, and group study workflows.',
    impact: ['50–70% prep time saved', '4 file formats', 'Real-time collaboration'],
    stack: ['FastAPI', 'OpenAI', 'Supabase', 'WebSockets', 'Python'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  {
    id: 'gluon',
    title: 'Gluon',
    subtitle: 'AI Outreach Platform · Full-Stack',
    category: 'AI Platforms',
    description:
      'Full-stack AI outreach and campaign platform. Ingests CSVs, segments audiences, generates AI recommendations and message drafts, manages campaign workflows — replacing 3–5 disconnected tools.',
    impact: ['45–65% campaign prep saved', 'CSV → draft in minutes', '3–5 tools replaced'],
    stack: ['FastAPI', 'React', 'TypeScript', 'OpenAI', 'Supabase', 'Docker'],
    links: { github: 'https://github.com/haiderrmasood99' },
    featured: true,
  },
  {
    id: 'kris',
    title: 'Kris AI Avatar',
    subtitle: 'Live Demo · Conversational AI',
    category: 'AI Platforms',
    description:
      'Live speaking AI avatar chaining OpenAI responses to D-ID streaming video output. Same-day prototype once credentials are configured. 25–50% engagement lift vs text-only demos in live presentations.',
    impact: ['Same-day prototype', '25–50% engagement lift', '3 integration layers'],
    stack: ['Node.js', 'Express', 'OpenAI', 'D-ID Streaming API'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  {
    id: 'ifs',
    title: 'IFS Reflection Assistant',
    subtitle: 'Wellness AI · Streamlit',
    category: 'AI Platforms',
    description:
      'Guided 11-step Internal Family Systems reflection assistant with session memory, step progression, safety flagging, and explicit wind-down behavior — a controlled wellness flow vs open-ended chat.',
    impact: ['11 guided steps', '15–30% drop-off reduction', 'Built-in safety flags'],
    stack: ['Python', 'Streamlit', 'OpenAI', 'Pydantic'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  {
    id: 'qr',
    title: 'QR Code Generator',
    subtitle: 'Product Module · Tap-Smart',
    category: 'AI Platforms',
    description:
      'Custom QR Code generation module built for Tap-Smart\'s smart card product. Integrated with their core development team with preprocessing steps validated through multiple live trial rounds.',
    impact: ['Production deployed', 'Smart card integration', 'Iterative QA validated'],
    stack: ['Python', 'REST API', 'QR Generation'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  // ── Automation & Agents ──────────────────────────────────────────────────────
  {
    id: 'tenders',
    title: 'Tender Intelligence Pipeline',
    subtitle: 'Procurement Automation · Azure',
    category: 'Automation & Agents',
    description:
      'Automated tender discovery and bid-triage pipeline. Scrapes procurement portals, downloads tender packs, extracts deadlines and requirements, and generates AI summaries — cutting first-pass triage by 50–75%.',
    impact: ['50–75% triage time saved', '4+ document types', 'Batch + idempotent'],
    stack: ['Python', 'Selenium', 'OpenAI', 'PyMuPDF', 'Azure', 'BeautifulSoup'],
    links: { github: 'https://github.com/haiderrmasood99' },
    featured: true,
  },
  {
    id: 'apgym',
    title: 'APGym',
    subtitle: 'AgriTech · Reinforcement Learning',
    category: 'Automation & Agents',
    description:
      'Agriculture simulation and RL package for testing crop-management decisions before committing field time. Wraps APSIM crop modeling into reproducible workflows with calibration gates and policy sweeps.',
    impact: ['60–80% setup time saved', '100+ policy scenarios/study', 'High reproducibility'],
    stack: ['Python', 'APSIM', 'Reinforcement Learning', 'Pytest'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  {
    id: 'visionmap',
    title: 'VisionMap',
    subtitle: 'Creative AI · Video Production',
    category: 'Automation & Agents',
    description:
      'Creative AI workflow turning reference images into structured cinematic prompts and composed video sequences across 5+ AI video systems (Runway, Kling, Pixverse, Vidu, Imagine Art).',
    impact: ['5 prompt variants per image', '5+ AI video systems', '40–60% storyboard time saved'],
    stack: ['Python', 'GPT-4o Vision', 'Pydantic', 'MoviePy', 'Jupyter'],
    links: { github: 'https://github.com/haiderrmasood99' },
  },
  // ── Research ──────────────────────────────────────────────────────────────────
  {
    id: 'thesis',
    title: 'CyclesGym',
    subtitle: 'MS Thesis · Crop RL Research',
    category: 'Research',
    description:
      'Pakistan-focused crop simulation + reinforcement learning thesis. 113 canonical evidence matrix rows, 42 ablation rows of frozen experiment results. Includes a farmer-facing React decision-support demo.',
    impact: ['113 evidence rows', '42 ablation rows', 'React + FastAPI demo'],
    stack: ['Python', 'CYCLES Simulator', 'Stable-Baselines3', 'PyTorch', 'WandB', 'React', 'FastAPI'],
    links: { github: 'https://github.com/haiderrmasood99' },
    featured: true,
  },
];

const categoryColors = {
  'Healthcare & Vision': { bg: 'rgba(139,31,47,0.1)',   text: 'hsl(350,62%,33%)',  border: 'rgba(139,31,47,0.24)'  },
  'AI Platforms':        { bg: 'rgba(49,82,103,0.11)',  text: 'hsl(203,36%,30%)',  border: 'rgba(49,82,103,0.24)'  },
  'Automation & Agents': { bg: 'rgba(104,90,52,0.12)',  text: 'hsl(44,34%,29%)',   border: 'rgba(104,90,52,0.26)'  },
  'Research':            { bg: 'rgba(88,24,35,0.1)',    text: 'hsl(350,55%,24%)',  border: 'rgba(88,24,35,0.24)'   },
};

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAll, setShowAll]               = useState(false);
  const [visibleCards, setVisibleCards]     = useState([]);
  const cardRefs = useRef([]);

  const filtered  = activeCategory === 'All'
    ? projectsData
    : projectsData.filter(p => p.category === activeCategory);
  const displayed = showAll ? filtered : filtered.slice(0, 6);
  const hasMore   = filtered.length > 6;

  // Re-observe whenever displayed list changes
  useEffect(() => {
    setVisibleCards([]);
    cardRefs.current = cardRefs.current.slice(0, displayed.length);
    const observers = cardRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCards(prev => prev.includes(i) ? prev : [...prev, i]);
          obs.disconnect();
        }
      }, { threshold: 0.1 });
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o && o.disconnect());
  }, [displayed.length, activeCategory, showAll]);

  return (
    <section className="projects section" id="projects">
      <h2 className="section__title-1"><span>Work.</span></h2>

      {/* ── Impact stats ─────────────────────────────────────── */}
      <div className="projects__stats">
        {[
          { n: '12+',  label: 'Projects shipped'   },
          { n: '4+',   label: 'Years building'      },
          { n: '1',    label: 'IEEE publication'    },
          { n: '30k',  label: 'Patients/day served' },
        ].map(s => (
          <div className="projects__stat" key={s.label}>
            <span className="projects__stat-number">{s.n}</span>
            <span className="projects__stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Category filter tabs ──────────────────────────────── */}
      <div className="projects__filters">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`projects__filter-btn${activeCategory === cat ? ' active' : ''}`}
            onClick={() => { setActiveCategory(cat); setShowAll(false); }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── Project grid ─────────────────────────────────────── */}
      <div className="projects__container container grid">
          {displayed.map((project, i) => {
          const col = categoryColors[project.category] || categoryColors['AI Platforms'];
          return (
            <article
              className={`projects__card${project.featured ? ' projects__card--featured' : ''} card-reveal${visibleCards.includes(i) ? ' card-reveal--visible' : ''}`}
              key={project.id}
              ref={el => cardRefs.current[i] = el}
              style={{ transitionDelay: `${(i % 3) * 80}ms` }}
            >
              {project.featured && (
                <span className="projects__featured-badge">Featured</span>
              )}

              <div className="projects__card-top">
                <span
                  className="projects__category-tag"
                  style={{ background: col.bg, color: col.text, border: `1px solid ${col.border}` }}
                >
                  {project.category}
                </span>
              </div>

              <div className="projects__content">
                <span className="projects__subtitle">{project.subtitle}</span>
                <h2 className="projects__title">{project.title}</h2>
                <p className="projects__description">{project.description}</p>

                {/* Impact metrics */}
                <div className="projects__impact">
                  {project.impact.map(m => (
                    <span className="projects__impact-pill" key={m}>
                      <i className="ri-sparkling-line"></i> {m}
                    </span>
                  ))}
                </div>

                {/* Tech stack */}
                <div className="projects__stack">
                  {project.stack.map(t => (
                    <span className="projects__stack-pill" key={t}>{t}</span>
                  ))}
                </div>
              </div>

              <div className="projects__buttons">
                <Link to={`/projects/${project.id}`} className="projects__link projects__link--primary">
                  <i className="ri-arrow-right-line"></i> Case Study
                </Link>
                {project.links.paper && (
                  <a href={project.links.paper} target="_blank" rel="noopener noreferrer" className="projects__link">
                    <i className="ri-article-line"></i> Paper
                  </a>
                )}
                <a href={project.links.github} target="_blank" rel="noopener noreferrer" className="projects__link">
                  <i className="ri-github-line"></i> GitHub
                </a>
                <button
                  className="projects__link projects__link--btn"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  <i className="ri-mail-line"></i> Discuss
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {/* ── Show more ────────────────────────────────────────── */}
      {hasMore && (
        <div className="projects__show-more">
          <button className="projects__show-more-btn" onClick={() => setShowAll(v => !v)}>
            <i className={`ri-arrow-${showAll ? 'up' : 'down'}-s-line`}></i>
            {showAll ? 'Show less' : `Show all ${filtered.length} projects`}
          </button>
        </div>
      )}
    </section>
  );
};

export default Projects;
